import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useScores } from '../hooks/useScores'

const CREW_OPTIONS = [
  { value: 'all', label: 'All Crews' },
  { value: 'audio', label: 'Audio' },
  { value: 'video', label: 'Video' },
  { value: 'stage', label: 'Stage' },
  { value: 'it', label: 'IT' },
] as const

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-card__value">{value}</div>
      <div className="stat-card__label">{label}</div>
      {sub && <div className="stat-card__sub">{sub}</div>}
    </div>
  )
}

function CrewFilter({
  active,
  onChange,
}: {
  active: string
  onChange: (crew: string) => void
}) {
  return (
    <div className="role-filter">
      <span className="role-filter__label">Filter</span>
      <div className="role-filter__chips">
        {CREW_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`role-chip ${active === opt.value ? 'role-chip--active' : ''}`}
            onClick={() => onChange(opt.value)}
            aria-pressed={active === opt.value}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function formatDate(isoString: string | null): string {
  if (!isoString) return '—'
  const d = new Date(isoString)
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function ScoreBar({ score, passed }: { score: number; passed: boolean }) {
  return (
    <div className="score-bar">
      <div className="score-bar__track">
        <div
          className={`score-bar__fill ${passed ? 'score-bar__fill--pass' : 'score-bar__fill--fail'}`}
          style={{ width: `${Math.min(score, 100)}%` }}
        />
      </div>
      <span className={`score-bar__value ${passed ? 'score-bar__value--pass' : 'score-bar__value--fail'}`}>
        {score}%
      </span>
    </div>
  )
}

function ModuleScoreRow({ module }: { module: import('../hooks/useScores').ModuleScore }) {
  return (
    <tr className={`score-row ${module.passed ? 'score-row--pass' : 'score-row--fail'}`}>
      <td>
        <Link to={module.path} className="score-module-link">
          <div className="score-module-title">{module.title}</div>
          <div className="score-module-meta">
            <span className="score-module-crew">{module.crew}</span>
            {module.teams.length > 0 && (
              <span className="score-module-teams">{module.teams.join(', ')}</span>
            )}
          </div>
        </Link>
      </td>
      <td>
        <ScoreBar score={module.averageScore} passed={module.passed} />
      </td>
      <td className="score-cell-attempts">{module.attempts}</td>
      <td className="score-cell-date">{formatDate(module.latestDate)}</td>
      <td>
        <span
          className={`status-badge ${module.passed ? 'status-completed' : 'status-in-progress'}`}
        >
          {module.passed ? 'Passed' : 'Review'}
        </span>
      </td>
    </tr>
  )
}

function ScoresTable({ modules }: { modules: import('../hooks/useScores').ModuleScore[] }) {
  if (modules.length === 0) {
    return (
      <div className="card" style={{ padding: '48px 24px', textAlign: 'center' }}>
        <p style={{ color: 'var(--muted)', margin: 0 }}>
          No knowledge check attempts yet. Complete modules to see your scores here.
        </p>
      </div>
    )
  }

  return (
    <div className="scores-table-wrapper">
      <table className="admin-table scores-table">
        <thead>
          <tr>
            <th scope="col">Module</th>
            <th scope="col">Score</th>
            <th scope="col">Attempts</th>
            <th scope="col">Last Attempt</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {modules.map((m) => (
            <ModuleScoreRow key={m.moduleId} module={m} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function ScoresPage() {
  const [activeCrew, setActiveCrew] = useState('all')
  const { data, loading, error } = useScores(
    activeCrew === 'all' ? undefined : { crew: activeCrew }
  )

  return (
    <div className="scores-page">
      <header className="crew-header">
        <p className="eyebrow">Performance</p>
        <h1>Knowledge Check Scores</h1>
        <p className="lead">Track your progress across modules and crews.</p>
      </header>

      {loading && <p>Loading scores…</p>}
      {error && <p className="error">Error: {error}</p>}

      {!loading && !error && data && (
        <>
          <div className="stat-cards">
            <StatCard
              label="Modules Attempted"
              value={String(data.stats.totalModulesAttempted)}
            />
            <StatCard
              label="Average Score"
              value={`${data.stats.overallAverage}%`}
              sub={`${data.stats.totalAttempts} total attempts`}
            />
            <StatCard
              label="Completion Rate"
              value={`${data.stats.completionRate}%`}
              sub={`${data.stats.completedCount} of ${data.stats.totalModulesAttempted} passed`}
            />
          </div>

          <CrewFilter active={activeCrew} onChange={setActiveCrew} />

          <h2>Module Breakdown</h2>
          <ScoresTable modules={data.modules} />

          {data.modules.length > 0 && (
            <p className="scores-legend">
              <span className="scores-legend__item">
                <span className="scores-legend__dot scores-legend__dot--pass" />
                Pass (≥70%)
              </span>
              <span className="scores-legend__item">
                <span className="scores-legend__dot scores-legend__dot--fail" />
                Review needed (&lt;70%)
              </span>
            </p>
          )}
        </>
      )}
    </div>
  )
}